import React, { useState, useEffect, useRef } from 'react';
import { MapPin, Search } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';

const LocationPicker = ({ value, onChange }) => {
  const [map, setMap] = useState(null);
  const [marker, setMarker] = useState(null);
  const [circle, setCircle] = useState(null);
  const [searchBox, setSearchBox] = useState(null);
  const mapRef = useRef(null);
  const searchInputRef = useRef(null);

  const [locationData, setLocationData] = useState({
    location: value?.location || '',
    latitude: value?.latitude || 28.6139, // Default: New Delhi
    longitude: value?.longitude || 77.2090,
    radius: value?.radius || 10
  });

  // Load Google Maps Script
  useEffect(() => {
    const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    
    if (!googleMapsApiKey) {
      console.error('Google Maps API key not found. Please add REACT_APP_GOOGLE_MAPS_API_KEY to your .env file');
      return;
    }

    if (!window.google) {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${googleMapsApiKey}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = initializeMap;
      document.head.appendChild(script);
    } else {
      initializeMap();
    }
  }, []);

  const initializeMap = () => {
    if (!mapRef.current || !window.google) return;

    const mapInstance = new window.google.maps.Map(mapRef.current, {
      center: { lat: locationData.latitude, lng: locationData.longitude },
      zoom: 12,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
    });

    // Create marker
    const markerInstance = new window.google.maps.Marker({
      position: { lat: locationData.latitude, lng: locationData.longitude },
      map: mapInstance,
      draggable: true,
      title: 'Service Location'
    });

    // Create circle
    const circleInstance = new window.google.maps.Circle({
      map: mapInstance,
      center: { lat: locationData.latitude, lng: locationData.longitude },
      radius: locationData.radius * 1000, // Convert km to meters
      fillColor: '#3B82F6',
      fillOpacity: 0.2,
      strokeColor: '#3B82F6',
      strokeOpacity: 0.8,
      strokeWeight: 2,
      editable: false
    });

    // Marker drag event
    markerInstance.addListener('dragend', (event) => {
      const lat = event.latLng.lat();
      const lng = event.latLng.lng();
      updateLocation(lat, lng);
      circleInstance.setCenter({ lat, lng });
    });

    // Initialize search box
    if (searchInputRef.current) {
      const searchBoxInstance = new window.google.maps.places.SearchBox(searchInputRef.current);
      mapInstance.addListener('bounds_changed', () => {
        searchBoxInstance.setBounds(mapInstance.getBounds());
      });

      searchBoxInstance.addListener('places_changed', () => {
        const places = searchBoxInstance.getPlaces();
        if (places.length === 0) return;

        const place = places[0];
        if (!place.geometry || !place.geometry.location) return;

        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        
        mapInstance.setCenter({ lat, lng });
        markerInstance.setPosition({ lat, lng });
        circleInstance.setCenter({ lat, lng });
        
        updateLocation(lat, lng, place.formatted_address);
      });

      setSearchBox(searchBoxInstance);
    }

    setMap(mapInstance);
    setMarker(markerInstance);
    setCircle(circleInstance);
  };

  const updateLocation = async (lat, lng, address = null) => {
    let locationName = address;

    // Reverse geocode if address not provided
    if (!locationName && window.google) {
      const geocoder = new window.google.maps.Geocoder();
      try {
        const result = await geocoder.geocode({ location: { lat, lng } });
        if (result.results[0]) {
          locationName = result.results[0].formatted_address;
        }
      } catch (error) {
        console.error('Geocoding error:', error);
      }
    }

    const newData = {
      location: locationName || `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
      latitude: lat,
      longitude: lng,
      radius: locationData.radius
    };

    setLocationData(newData);
    onChange(newData);
  };

  const handleRadiusChange = (e) => {
    const radius = parseInt(e.target.value) || 10;
    const newData = { ...locationData, radius };
    setLocationData(newData);
    onChange(newData);

    if (circle) {
      circle.setRadius(radius * 1000);
    }
  };

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          
          if (map && marker && circle) {
            map.setCenter({ lat, lng });
            marker.setPosition({ lat, lng });
            circle.setCenter({ lat, lng });
            updateLocation(lat, lng);
          }
        },
        (error) => {
          console.error('Geolocation error:', error);
          alert('Unable to get your location. Please search for your location manually.');
        }
      );
    } else {
      alert('Geolocation is not supported by your browser.');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center space-x-2">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Search for your location..."
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <Button
          type="button"
          onClick={getCurrentLocation}
          variant="outline"
          className="whitespace-nowrap"
        >
          <MapPin className="h-4 w-4 mr-2" />
          Use Current
        </Button>
      </div>

      <div 
        ref={mapRef} 
        className="w-full h-96 rounded-lg border-2 border-gray-200 shadow-sm"
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Selected Location
          </label>
          <Input
            type="text"
            value={locationData.location}
            readOnly
            className="bg-gray-50"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Service Radius (km)
          </label>
          <div className="flex items-center space-x-3">
            <Input
              type="range"
              min="1"
              max="50"
              value={locationData.radius}
              onChange={handleRadiusChange}
              className="flex-1"
            />
            <span className="text-sm font-semibold text-gray-700 min-w-[60px]">
              {locationData.radius} km
            </span>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            You'll receive requests from students within this radius
          </p>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <MapPin className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-800">
            <p className="font-semibold mb-1">How it works:</p>
            <ul className="list-disc list-inside space-y-1 text-xs">
              <li>Search for your location or drag the marker on the map</li>
              <li>Adjust the radius slider to set your service area</li>
              <li>The blue circle shows your service coverage area</li>
              <li>You'll only receive requests from students within this area</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LocationPicker;
